/**
 * Лента новостей HUB 2.0 (волна W5) — чистые функции (порт логики CRM
 * frontend/src/pages/news/*: категории со счётчиками, поиск, месячная сетка
 * календаря). Без React; спеки — news-utils.test.ts.
 */

/** Минимум полей элемента ленты, нужный фильтрам. */
export interface FeedLike {
  id: string;
  title: string;
  excerpt: string | null;
  categoryName: string | null;
  publishedAt: string | null;
  isPinned: boolean;
  priority: 'NORMAL' | 'IMPORTANT' | 'REQUIRES_ACTION' | string;
  requiresAck: boolean;
}

export const ALL = '__all__';
export const UNREAD = '__unread__';
export const IMPORTANT = '__important__';
export const NEED_ACK = '__ack__';

export function isImportant(n: Pick<FeedLike, 'isPinned' | 'priority'>): boolean {
  return n.isPinned || n.priority === 'IMPORTANT' || n.priority === 'REQUIRES_ACTION';
}


/** Чипы категорий: реальные имена из ленты + счётчики (всего / непрочитано), по убыванию числа. */
export function categoryCountsWithRead(items: FeedLike[], readIds: Set<string>): Array<{ name: string; count: number; unread: number }> {
  const by = new Map<string, { count: number; unread: number }>();
  for (const n of items) {
    const k = n.categoryName?.trim() || 'Без категории';
    const v = by.get(k) ?? { count: 0, unread: 0 };
    v.count++;
    if (!readIds.has(n.id)) v.unread++;
    by.set(k, v);
  }
  return [...by.entries()].map(([name, v]) => ({ name, ...v })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'ru'));
}

/** Фильтр ленты: чип (категория / непрочитанные / важные / ждут подтверждения) + поиск. Закреплённые — сверху. */
export function filterFeed<T extends FeedLike>(items: T[], opts: { chip: string; q: string; readIds: Set<string> }): T[] {
  const q = opts.q.trim().toLowerCase();
  const out = items.filter((n) => {
    if (opts.chip === UNREAD && opts.readIds.has(n.id)) return false;
    if (opts.chip === IMPORTANT && !isImportant(n)) return false;
    if (opts.chip === NEED_ACK && !(n.requiresAck && !opts.readIds.has(n.id))) return false;
    if (![ALL, UNREAD, IMPORTANT, NEED_ACK].includes(opts.chip) && (n.categoryName?.trim() || 'Без категории') !== opts.chip) return false;
    if (q && !`${n.title} ${n.excerpt ?? ''}`.toLowerCase().includes(q)) return false;
    return true;
  });
  return out.sort((a, b) => Number(b.isPinned) - Number(a.isPinned));
}

/** Месячная сетка календаря с понедельника: 5–6 недель дат 'YYYY-MM-DD'. */
export function monthGrid(year: number, month0: number): Array<{ day: string; inMonth: boolean }> {
  const first = new Date(Date.UTC(year, month0, 1));
  const back = (first.getUTCDay() + 6) % 7;
  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - back);
  const lastDay = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const cells = Math.ceil((back + lastDay) / 7) * 7;
  const out: Array<{ day: string; inMonth: boolean }> = [];
  for (let i = 0; i < cells; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    out.push({ day: d.toISOString().slice(0, 10), inMonth: d.getUTCMonth() === month0 });
  }
  return out;
}

/** Локальный день публикации 'YYYY-MM-DD' (по времени браузера — как видит читатель). */
export function localDay(iso: string): string {
  const d = new Date(iso);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
